import { requestApi } from "../api/api";
import {
	clearSession,
	getSession,
	setSession,
	type ServixSession,
	type ServixUserType,
} from "../session/session";

type LoginCredentials = {
	email: string;
	password: string;
};

// Shape after api.ts normalization of the "session" resource type:
// { id, user: { id, names, email, type_user, providers, ... }, token }
type SessionLoginResponse = {
	id?: number;
	token?: string;
	user?: {
		id: number;
		email?: string | null;
		names?: string | null;
		type_user?: unknown;
		providers?: { id?: number; verified_provider?: boolean | null } | null;
	};
};

type UserResponse = {
	user?: {
		id: number;
		email?: string | null;
		type_user?: unknown;
		names?: string | null;
		last_names?: string | null;
		providers?:
			| { id?: number; verified_provider?: boolean | null }
			| Array<{ id?: number; verified_provider?: boolean | null }>
			| null;
 	};
	id?: number;
	email?: string | null;
	type_user?: unknown;
	names?: string | null;
	last_names?: string | null;
	providers?:
		| { id?: number; verified_provider?: boolean | null }
		| Array<{ id?: number; verified_provider?: boolean | null }>
		| null;
};

function normalizeAuthErrorMessage(raw: string): string {
	// El backend a veces manda errores como "ForbiddenException: Tu cuenta...".
	const normalized = raw.includes(":") ? raw.split(":").slice(1).join(":").trim() : raw;
	return normalized || raw;
}

function normalizeUserType(value: unknown): ServixUserType | null {
	if (typeof value !== "string") return null;
	const raw = value.trim().toLowerCase();
	if (!raw) return null;
	if (raw === "cliente" || raw === "client") return "Cliente";
	if (raw === "proveedor" || raw === "provider") return "Proveedor";
	if (raw === "gerente" || raw === "manager") return "Gerente";
	if (raw === "soporte" || raw === "support") return "Soporte";
	return null;
}

function pickUserEntity(payload: UserResponse): {
	id?: number;
	email?: string | null;
	type_user?: unknown;
	names?: string | null;
	last_names?: string | null;
	providers?:
		| { id?: number; verified_provider?: boolean | null }
		| Array<{ id?: number; verified_provider?: boolean | null }>
		| null;
} {
	if (payload?.user && typeof payload.user === "object") {
		return payload.user;
	}

	return {
		id: payload.id,
		email: payload.email,
		type_user: payload.type_user,
		names: payload.names,
		last_names: payload.last_names,
		providers: payload.providers,
	};
}

function getVerifiedFromUserEntity(entity: {
	providers?:
		| { id?: number; verified_provider?: boolean | null }
		| Array<{ id?: number; verified_provider?: boolean | null }>
		| null;
}): boolean | null {
	const providers = entity.providers;
	if (!providers) return null;

	if (Array.isArray(providers)) {
		const first = providers.find((item) => typeof item?.id === "number");
		if (!first) return null;
		if (first.verified_provider === true) return true;
		if (first.verified_provider === false) return false;
		return null;
	}

	if (providers.verified_provider === true) return true;
	if (providers.verified_provider === false) return false;
	return null;
}

async function getProviderVerificationStatus(userId: number): Promise<boolean | null> {
	try {
		const userPayload = await requestApi<UserResponse>("GET", `/users/${userId}`);
		const userEntity = pickUserEntity(userPayload);
		return getVerifiedFromUserEntity(userEntity);
	} catch {
		return null;
	}
}

async function assertVerifiedProvider(userId: number): Promise<void> {
	const verified = await getProviderVerificationStatus(userId);
	if (verified === false) throw new Error("Proveedor no verificado");
}

export async function login(credentials: LoginCredentials): Promise<ServixSession> {
	try {
		const loginRes = await requestApi<SessionLoginResponse>(
			"POST",
			"/session/login",
			credentials,
		);

		const userId = loginRes.user?.id;
		if (!userId) throw new Error("No se pudo obtener el usuario de la sesión");

		// El backend ya valida que el proveedor esté verificado antes de devolver 200.
		// Usamos los datos del login directamente, sin una segunda llamada a /users/:id.
		const typeUser = normalizeUserType(loginRes.user?.type_user) ?? null;
		const name = loginRes.user?.names ?? null;

		const session: ServixSession = {
			user: {
				id: userId,
				email: loginRes.user?.email ?? credentials.email,
				type_user: typeUser,
				name,
			},
		};

		setSession(session);
		// Notifica al PendingReviewProvider que el login fue exitoso
		window.dispatchEvent(new CustomEvent("servix_post_login"));
		return session;
	} catch (err) {
		clearSession();
		const raw = err instanceof Error ? err.message : "No se pudo iniciar sesión";
		throw new Error(normalizeAuthErrorMessage(raw));
	}
}

export async function hydrateSessionUser(): Promise<ServixSession | null> {
	const current = getSession();
	if (!current?.user?.id) return null;
	// Si ya tenemos el tipo, no hacemos nada (pero validamos proveedor verificado).
	if (current.user.type_user) {
		if (current.user.type_user === "Proveedor") {
			try {
				await assertVerifiedProvider(current.user.id);
			} catch {
				clearSession();
				return null;
			}
		}
		return current;
	}

	try {
		const userPayload = await requestApi<UserResponse>(
			"GET",
			`/users/${current.user.id}`,
		);
		const userEntity = pickUserEntity(userPayload);
		const typeUser = normalizeUserType(userEntity.type_user) ?? null;
		const name = [userEntity.names, userEntity.last_names].filter(Boolean).join(" ") || null;
		if (typeUser === "Proveedor") {
			const verifiedFromUser = getVerifiedFromUserEntity(userEntity);
			if (verifiedFromUser === false) throw new Error("Proveedor no verificado");
			if (verifiedFromUser !== true) await assertVerifiedProvider(current.user.id);
		}
		const next: ServixSession = {
			user: {
				...current.user,
				type_user: typeUser,
				name,
			},
		};
		setSession(next);
		return next;
	} catch {
		return current;
	}
}

export async function logout(): Promise<void> {
	clearSession();
	window.dispatchEvent(new CustomEvent("servix_logout"));
	try {
		await requestApi("POST", "/session/logout", {});
	} catch {
		// Silent fail — local session already cleared
	}
}
